import { useMemo } from 'react';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { SMLNavBadgeSource, smlNavGroups, SMLNavGroupId } from '../smlNavigation';
import { useFailedPostsCount } from './useFailedPostsCount';
import { useChannelsNeedingReconnect } from './useChannelsNeedingReconnect';
import { type CatalogSetupMissing, useCatalogSetupNeeded } from './useCatalogSetupNeeded';
import { useKitWorkBadge } from './useKitWorkBadge';
import { useOutreachInboxCount } from '../../outreach/hooks/useOutreachInboxCount';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';

/**
 * Navigation v2 (owner, 2026-10-23): a menu badge is a red count of things that wait for the
 * person, or the hollow violet setup dot of Catalog. "2 working" is not a menu badge: it is the
 * chip on Studio.
 */
export type SMLNavBadge =
  | { kind: 'count'; count: number; source: SMLNavBadgeSource }
  | { kind: 'setup'; source: SMLNavBadgeSource; reason?: CatalogSetupMissing };

export interface SMLNavBadges {
  /** By item id. An item with nothing to say has no entry. */
  items: Partial<Record<string, SMLNavBadge>>;
  /** The groups that hold an attention count, for the pip on the rail and the bottom bar. */
  groupsWithAttention: SMLNavGroupId[];
  /**
   * The social accounts that need a new sign-in. The account card (pip) and the Connections row
   * of the account menu read it. 0 when none.
   */
  accountAttention: number;
}

export interface UseNavBadgesOptions {
  /** `false` when no navigation is drawn (a native app's web view): the reads are skipped. */
  enabled?: boolean;
}

/** Every badge the navigation shows, read once and shared by the sidebar and the mobile bar. */
export function useNavBadges(options?: UseNavBadgesOptions): SMLNavBadges {
  const selectedAccountId = useAccountId();
  // Each source skips its request when it has no account, so a disabled read asks for nothing.
  const accountId = (options?.enabled ?? true) ? selectedAccountId : null;
  const failedPosts = useFailedPostsCount(accountId);
  const channelsNeedingReconnect = useChannelsNeedingReconnect(accountId);
  const catalogBadge = useCatalogSetupNeeded(accountId);
  const kitWork = useKitWorkBadge(accountId);
  // QA finding F31: the Inbox count of the demo artist (LaziiBee) is not the person's own mail, so a
  // new user in the tour sees no "Inbox 2". The read is skipped while the demo artist is active.
  const isDemo = useIsDemoAccount();
  const outreachInbox = useOutreachInboxCount(isDemo ? null : accountId);

  return useMemo(() => {
    const bySource: Partial<Record<SMLNavBadgeSource, SMLNavBadge>> = {};
    if (failedPosts !== undefined && failedPosts > 0) {
      bySource.failedPosts = { kind: 'count', count: failedPosts, source: 'failedPosts' };
    }
    // design: your-intelligence h2 — the Reconnect count wins over the setup dot.
    if (catalogBadge.reconnect > 0) {
      bySource.catalogSetup = { kind: 'count', count: catalogBadge.reconnect, source: 'catalogSetup' };
    } else if (catalogBadge.setup) {
      bySource.catalogSetup = {
        kind: 'setup',
        source: 'catalogSetup',
        ...(catalogBadge.missing === null ? {} : { reason: catalogBadge.missing }),
      };
    }
    // design: navigation-v2 c 3e — only the failed work is a menu badge (on Studio).
    if (kitWork !== undefined && kitWork.newFailed > 0) {
      bySource.kitWork = { kind: 'count', count: kitWork.newFailed, source: 'kitWork' };
    }

    // design: outreach a1 — the replies that wait for the person.
    if (outreachInbox !== undefined && outreachInbox > 0) {
      bySource.outreachInbox = { kind: 'count', count: outreachInbox, source: 'outreachInbox' };
    }

    const items: Partial<Record<string, SMLNavBadge>> = {};
    const groupsWithAttention: SMLNavGroupId[] = [];
    for (const group of smlNavGroups) {
      for (const item of group.items) {
        const badge = item.badge === undefined ? undefined : bySource[item.badge];
        if (badge === undefined) {
          continue;
        }
        items[item.id] = badge;
        if (badge.kind === 'count' && !groupsWithAttention.includes(group.id)) {
          groupsWithAttention.push(group.id);
        }
      }
    }
    return { items, groupsWithAttention, accountAttention: channelsNeedingReconnect ?? 0 };
  }, [failedPosts, channelsNeedingReconnect, catalogBadge, kitWork, outreachInbox]);
}
